import { b64utoutf8, KJUR } from "jsrsasign";
import Cookies from "js-cookie";
import allowedMutation from "./allowedMutation";
import * as Sentry from "@sentry/vue";
import FipaListener from "@/utils/listenerUtility";

class AuthService {
  constructor() {
    this.visitorRoutes = [];
    this.clientId = null;
    this.user = {
      id: null,
      name: null,
      permissionsGiven: [],
      canPerformMutationsInScopeAny: [],
      canAccessEntitiesInScopeAny: [],
      allowedEntities: [],
      allowedRoutes: []
    };
  }

  get userId() {
    return this.user.id;
  }

  get userName() {
    return this.user.name;
  }

  set userName(v) {
    if (v) {
      this.user.name = v;
      FipaListener.emit("nameChange");
    }
  }

  checkUserCanPerformMutationsInScopeAny(mutation) {
    return allowedMutation(this.user.canPerformMutationsInScopeAny, mutation);
  }

  canAccessEntitiesInScopeAny(entity) {
    return allowedMutation(this.user.canAccessEntitiesInScopeAny, entity);
  }

  allowedRoutes() {
    return this.user.allowedRoutes ?? [];
  }

  allowedEntities() {
    return this.user.allowedEntities ?? [];
  }

  allowedMutations() {
    return this.user.canPerformMutationsInScopeAny ?? [];
  }

  permissionsGiven() {
    return this.user.permissionsGiven ?? [];
  }

  setSession(token) {
    Cookies.set("token", token, {
      secure: process.env.NODE_ENV == "production",
      sameSite: "strict",
      path: "/"
    });
  }

  setUserData(user) {
    this.user = {
      id: user.id,
      name: user.name,
      permissionsGiven: user.permissionsGiven ?? [],
      canPerformMutationsInScopeAny: user.canPerformMutationsInScopeAny ?? [],
      canAccessEntitiesInScopeAny: user.canAccessEntitiesInScopeAny ?? [],
      allowedEntities: user.allowedEntities ?? [],
      allowedRoutes: user.allowedRoutes ?? []
    };

    Sentry.setContext("user", {
      id: user.id,
      "fipa-cid": this.getClientId()
    });

    Sentry.getCurrentScope().setUser({ id: user.id });
  }

  tokenExists() {
    return Cookies.get("token") ? true : false;
  }

  removeToken() {
    Cookies.remove("token");
  }

  getToken(getAsObject) {
    const token = Cookies.get("token");
    if (token) {
      if (getAsObject)
        return KJUR.jws.JWS.readSafeJSONString(b64utoutf8(token.split(".")[1]));
      return token;
    } else {
      if (getAsObject) return {};
      return null;
    }
  }

  async logout(options = {}) {
    const { sessionExpired = false, manualLogout = false } = options;

    if (process.env.VUE_APP_HELPSCOUT_KEY && window.Beacon)
      window.Beacon("logout");
    if (manualLogout == true) {
      sessionStorage.setItem("loggedOut", true);
    } else if (sessionExpired == true) {
      sessionStorage.setItem("sessionExpired", true);
    }

    Sentry.setContext("user", {
      id: null,
      "fipa-cid": Cookies.get("fipa-cid")
    });

    Sentry.getCurrentScope().setUser(null);

    if (process.env.VUE_APP_OPENID_END_SESSION_ENDPOINT) {
      await fetch(process.env.VUE_APP_OPENID_END_SESSION_ENDPOINT, {
        method: "GET",
        cache: "no-cache",
        mode: "no-cors",
        credentials: "include"
      }).catch(err => {
        Sentry.captureException(err);
      });
    }

    Cookies.remove("token");
    localStorage.removeItem("user");

    this.user = {};
    // navigate to the home route
    if (window.location.pathname == "/") {
      window.location.reload();
    } else {
      window.location.pathname = "/";
    }
  }

  get isAuthenticated() {
    return this.tokenStatus;
  }

  get isUserLoggedIn() {
    return Boolean(this.tokenStatus);
  }

  get isUserNotLoggedIn() {
    return !this.tokenStatus;
  }

  checkTokenStillAuthenticated() {
    return this.isAuthenticated;
  }

  get tokenStatus() {
    // Check whether the current time is past the
    // access token's expiry time
    const token = Cookies.get("token");
    if (token) {
      const decodedToken = KJUR.jws.JWS.readSafeJSONString(
        b64utoutf8(token.split(".")[1])
      );
      return validateToken(decodedToken);
    }
    return false;
  }

  generateClientId() {
    if (!Cookies.get("fipa-cid")) {
      let fipaCid = "";

      // clear cookie with old name
      if (Cookies.get("feds-cid")) {
        fipaCid = Cookies.get("feds-cid");
        Cookies.set("feds-cid", "", { expires: -1 });
      } else {
        // generate new client id
        let firstPart = (Math.random() * 46656) | 0;
        let secondPart = (Math.random() * 46656) | 0;
        firstPart = ("000" + firstPart.toString(36)).slice(-3);
        secondPart = ("000" + secondPart.toString(36)).slice(-3);
        fipaCid = firstPart + secondPart;
      }
      Cookies.set("fipa-cid", fipaCid, {
        domain: "." + window.location.hostname,
        path: "/",
        sameSite: "Lax",
        expires: 365,
        secure: true
      });
    }
  }

  getClientId() {
    if (this.clientId) {
      return this.clientId;
    }

    this.generateClientId();
    this.clientId = Cookies.get("fipa-cid");
    return this.clientId;
  }

  canAccessRoute(routeName) {
    // console.log("Checking the access to route: " + routeName);
    if (this.allowedRoutes()?.includes(routeName)) {
      //console.log("Is allowed: " + routeName);
      return true;
    } else {
      //console.log("Not allowed: " + routeName);
      return false;
    }
  }

  canAccessEntity(entityName) {
    // entity:Event(.*)(?=;field:test)
    if (this.permissionsGiven().find(p => p.indexOf("entity:" + entityName))) {
      return true;
    } else {
      return false;
    }
  }

  canAccessField(entityName, fieldName, scope = null) {
    /*
    console.log(
      "Checking the access to field: " +
        entityName +
        "." +
        fieldName +
        " in scope: " +
        scope
    ); */
    let scopePart = "";
    if (scope) {
      scopePart = "scope:" + scope + ";";
    }
    const regex = new RegExp(
      scopePart + "entity:" + entityName + "(.*)(?=;field:" + fieldName + ")",
      "g"
    );

    if (
      this.permissionsGiven().find(p => (p.search(regex) == -1 ? false : true))
    ) {
      return true;
    } else {
      return false;
    }
  }

  // filters out headers which have guardedByField set but no permission to this field in "any" scope
  // in order to guard header by guardedByField, corresponding header must have guardedByField property set in format '[EntityName].[fieldName]'
  allowedHeaders(headers) {
    if (Array.isArray(headers))
      return headers.filter(
        h =>
          !h.guardedByField ||
          this.canAccessField(
            h.guardedByField.split(".")[0],
            h.guardedByField.split(".")[1],
            "Any"
          )
      );
  }
}

export default new AuthService();

export function validateToken(decodedToken) {
  return decodedToken && Date.now() / 1000 < decodedToken.exp;
}
